//NEW: UI_REVAMP
import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Button,
	Flex,
	Input,
	Text,
	VStack,
} from '@chakra-ui/react';
import { getOddsAmount } from '../../utils/getOddsAmount';

import ImgComp from '../../utils/ImgComp';
import { currencyFormat } from '../../utils/numberFormat';
import GradientBox from '../box/GradientBox';

const Cell = ({ children, flex = 1, align = 'flex-start' }) => (
	<Flex flex={flex} align='center' justify={align} minW={0} w='100%'>
		{children}
	</Flex>
);

const BetListHeader = ({ title, gameType, betType, minBetAmount, isImage, isResult }) => {
	if (title) return title;

	if (gameType && betType) {
		const isStraight = betType.toLowerCase() === 'straight';

		return (
			<VStack spacing={1} alignItems='start' py={2}>
				<Flex gap={1} alignItems='center'>
					<ImgComp svgName={gameType.toLowerCase()} w={6} h='auto' />
					<Text fontSize='16px' fontWeight={600} color='black' pb={isImage ? '15px' : 0}>
						{gameType}
					</Text>
					<Box
						display='flex'
						alignItems='center'
						justifyContent='center'
						bgGradient={
							isStraight
								? 'linear-gradient(180deg, #FCE007 0%, #F99D20 100%)'
								: 'linear-gradient(180deg, #D81985 0%, #E81A1E 100%)'
						}
						h={isImage ? 7 : 'auto'}
						color={isStraight ? 'black' : 'white'}
						borderRadius={4}
						px={3}
						py={isImage ? 0 : 0.5}
						fontSize='16px'
						fontWeight={600}
					>
						<Text pb={isImage ? '15px' : 0}>{betType}</Text>
					</Box>
				</Flex>
				{!isResult && minBetAmount !== undefined && (
					<Flex gap={1} justifyContent='center' alignItems='center'>
						<ImgComp svgName='minimum-alert' height={3} width={3} />
						<Text color='#707070' fontSize='12px'>
							Minimum Bet: {minBetAmount} PHP
						</Text>
					</Flex>
				)}
			</VStack>
		);
	}

	return null;
};

const BetListAccordion = ({
	title,
	data,
	columns,
	onPriceChange,
	removeBetting,
	isResult,
	gameType,
	betType,
	isImage = false,
	invalidBetIds = [],
	minBetAmounts,
}) => {
	return (
		<GradientBox borderRadius={8}>
			<Accordion
				allowMultiple
				index={isImage ? [0] : undefined}
				defaultIndex={!isImage ? [0] : undefined}
				borderRadius={8}
				overflow='hidden'
				bgGradient='linear-gradient(180deg, #FFF 0%, #E5EDF9 100%), linear-gradient(180deg, #FFF 0%, #FFDBE8 100%)'
			>
				<AccordionItem border='none'>
					{({ isExpanded }) => (
						<>
							{/* Header */}
							<AccordionButton
								py={0.5}
								px={2}
								_hover={{ bg: 'transparent' }}
								display='flex'
								justifyContent='space-between'
								alignItems='center'
							>
								<BetListHeader
									title={title}
									gameType={gameType}
									betType={betType}
									isImage={isImage}
									minBetAmount={minBetAmounts?.[gameType]}
									isResult={isResult}
								/>

								<Box
									display={isImage ? 'none' : 'inline-flex'}
									transition='transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
									transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
								>
									<ImgComp svgName='chevron-down' />
								</Box>
							</AccordionButton>

							{/* Content */}
							<AccordionPanel px={2} py={0}>
								<Box w='full' h='1px' bg='#ECD1DA' my={1} />

								<Box overflowX='auto'>
									<VStack spacing={0.5} align='stretch'>
										{/* Table Header */}
										<Flex fontSize='10px' color='#666' fontWeight='bold' gap={2.5} w='full'>
											{columns.map((col, idx) => (
												<Cell key={idx} flex={col.flex ?? 1} align={col.align ?? 'flex-start'}>
													{col.label}
												</Cell>
											))}
											{!isResult && <Box w={6} />}
										</Flex>

										{/* Table Rows */}
										{data.map((bet) => (
											<Flex
												key={bet.id}
												alignItems='center'
												fontSize='14px'
												gap={2.5}
												pb={2}
												color='#520612'
												fontWeight={600}
											>
												{columns.map((col, idx) => {
													const flexValue = col.flex ?? 1;
													const align = col.align ?? 'flex-start';

													/** DIGIT */
													if (col.key === 'digit') {
														return (
															<Cell key={idx} flex={flexValue} align={align}>
																<Flex align='center' gap={1} letterSpacing={2} whiteSpace='nowrap'>
																	<Text minWidth='55px'>{bet[col.key]}</Text>
																	{bet.isLuckyPick && <ImgComp svgName='lucky' />}
																</Flex>
															</Cell>
														);
													}

													/** DRAW TIME */
													if (col.key === 'drawTime') {
														return (
															<Cell key={idx} flex={flexValue} align={align}>
																<Text textTransform='uppercase'>{bet.timeName}</Text>
															</Cell>
														);
													}

													/** ODDS */
													if (col.key === 'odds') {
														return (
															<Cell key={idx} flex={flexValue} align={align}>
																<Text>{getOddsAmount(bet)}</Text>
															</Cell>
														);
													}

													/** BET PRICE */
													if (col.key === 'betPrice') {
														return (
															<Cell key={idx} flex={flexValue} align={align}>
																{isResult ? (
																	<Box h={8} display='flex' alignItems='center' px={2}>
																		{bet[col.key]}
																	</Box>
																) : (
																	<Input
																		h={8}
																		type='tel'
																		value={currencyFormat(Number(bet[col.key])) ?? ''}
																		onChange={(e) => onPriceChange(bet.id, e.target.value)}
																		w='full'
																		bg={
																			invalidBetIds.includes(bet.id)
																				? 'radial-gradient(46.23% 63.98% at 50.14% 100%, #F4C0E4 0%, rgba(255, 255, 255, 0.00) 100%), linear-gradient(181deg, #FCF6F6 50%, #FFB2BA 99.47%)'
																				: '#F2F6FF'
																		}
																		textAlign='right'
																		maxW='120px'
																		px={2}
																		border={
																			invalidBetIds.includes(bet.id) ? '2px solid' : '1px solid'
																		}
																		borderColor={
																			invalidBetIds.includes(bet.id) ? '#D7212C' : '#83AFFF'
																		}
																		borderRadius={8}
																		fontSize='14px'
																		fontWeight={600}
																		_hover={{}}
																		_focus={{
																			bgGradient:
																				'linear-gradient(180deg, #BEB5FB 0%, #8674FE 25%, #8674FE 75%, #BEB5FB 100%)',
																			border: '1px solid #5317E9',
																			color: 'white',
																			ring: 0,
																		}}
																		maxLength={11} //including comma
																	/>
																)}
															</Cell>
														);
													}

													return (
														<Cell key={idx} flex={flexValue} align={align}>
															{bet[col.key]}
														</Cell>
													);
												})}

												{/* Delete */}
												{!isResult && (
													<Button
														p={0}
														bg='transparent'
														_hover={{ bg: 'transparent' }}
														onClick={() => removeBetting(bet.id)}
														w={6}
														h={6}
														minW={0}
														minH={0}
													>
														<ImgComp svgName='remove' w='full' h='full' />
													</Button>
												)}
											</Flex>
										))}
									</VStack>
								</Box>
							</AccordionPanel>
						</>
					)}
				</AccordionItem>
			</Accordion>
		</GradientBox>
	);
};

export default BetListAccordion;
